/**
 * @file use-resizable.ts
 * @description Provides a custom hook to handle horizontal resizing logic for the chat canvas.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_MIN_CANVAS_WIDTH = 300;

/**
 * Manages the width state and drag event listeners for a resizable element.
 * * @param initialWidth - The starting width of the element in pixels.
 * @param isFullPage - If true, dragging and resizing actions are disabled.
 * @param minWidth - The minimum width in pixels while dragging. Defaults to 300.
 * @returns An object containing the current `width` and the `startResizing` mouse event handler.
 */
export function useResizable(initialWidth: number, isFullPage: boolean, minWidth: number = DEFAULT_MIN_CANVAS_WIDTH) {
  const [width, setWidth] = useState(initialWidth);
  const isResizing = useRef(false);

  const startResizing = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (isFullPage) return;

      isResizing.current = true;
      document.body.style.userSelect = 'none';

      const startX = e.clientX;
      const startWidth = width;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isResizing.current) return;
        const deltaX = startX - moveEvent.clientX;
        setWidth(Math.max(minWidth, startWidth + deltaX));
      };

      const handleMouseUp = () => {
        isResizing.current = false;
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [width, isFullPage, minWidth],
  );

  // Safety cleanup in case the component unmounts while dragging
  useEffect(() => {
    return () => {
      document.body.style.userSelect = '';
    };
  }, []);

  return { width, startResizing };
}
